import * as XLSX from 'xlsx';
import { BankTransaction, PaymentMode } from '../types/reconciliation';

const MONTH_MAP: Record<string, string> = {
  jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
  jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12',
};

function parseDate(val: any): string {
  if (!val) return '';
  if (typeof val === 'number') {
    const d = XLSX.SSF.parse_date_code(val);
    return `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
  }
  const s = String(val).trim();

  // DD/MMM/YYYY  (e.g. 02/Mar/2026)
  const mAlpha = s.match(/^(\d{1,2})[\/\-]([A-Za-z]{3})[\/\-](\d{4})$/);
  if (mAlpha) {
    const mm = MONTH_MAP[mAlpha[2].toLowerCase()];
    if (mm) return `${mAlpha[3]}-${mm}-${mAlpha[1].padStart(2, '0')}`;
  }

  // DD/MM/YYYY or DD-MM-YYYY
  const mNum = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (mNum) return `${mNum[3]}-${mNum[2].padStart(2, '0')}-${mNum[1].padStart(2, '0')}`;

  // Try native parse — use local calendar date (avoid UTC shift from toISOString)
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  return s;
}

function parseAmount(val: any): number {
  if (val === null || val === undefined || val === '') return 0;
  if (typeof val === 'number') return val;
  return parseFloat(String(val).replace(/,/g, '')) || 0;
}

function extractUTR(narration: string): string | undefined {
  // RTGS-UTR-... or NEFT-UTR-... or IMPS-UTR-...
  const dashSplit = narration.match(/^(?:RTGS|NEFT|IMPS)[\-]([A-Z0-9]+)[\-]/i);
  if (dashSplit) return dashSplit[1];

  const patterns = [
    /UTR[:\s]?([A-Z0-9]+)/i,
    /([A-Z]{4}R[0-9]{15,})/,
    /([A-Z]{4}N[0-9]{15,})/,
  ];
  for (const p of patterns) {
    const m = narration.match(p);
    if (m) return m[1];
  }
  return undefined;
}

function extractPaymentMode(narration: string): PaymentMode {
  const upper = narration.toUpperCase();
  if (upper.startsWith('RTGS') || upper.includes('RTGS')) return 'RTGS';
  if (upper.startsWith('NEFT') || upper.includes('NEFT')) return 'NEFT';
  if (upper.startsWith('IMPS') || upper.includes('IMPS')) return 'IMPS';
  if (upper.includes('UPI')) return 'UPI';
  return 'OTHER';
}

function extractCounterparty(narration: string): string | undefined {
  if (!narration) return undefined;

  // Split on common bank narration separators: / | -
  const segments = narration.split(/[\/|\-]/).map(s => s.trim()).filter(Boolean);
  if (segments.length === 0) return undefined;

  // Discard segments that look like codes (IFSC, UTR, account numbers, mode tokens, amounts)
  const NOISE = /^(INF|RTGS|NEFT|IMPS|UPI|TXN|REF|CR|DR|NA|N\/A|RENT|PAY|PAYMENT|INV|GST|TDS)$/i;
  const looksLikeIfsc = (s: string) => /^[A-Z]{4}0[A-Z0-9]{6}$/i.test(s);
  const looksLikeUtr = (s: string) => /^[A-Z]{0,4}\d{10,}$/i.test(s) || /^[A-Z0-9]{12,}$/i.test(s);
  const looksLikeNumeric = (s: string) => /^\d+(\.\d+)?$/.test(s);
  const looksLikeMonth = (s: string) => /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*(\s+\d{2,4})?$/i.test(s);

  const isCandidate = (s: string) => {
    if (s.length < 3) return false;
    if (NOISE.test(s)) return false;
    if (looksLikeIfsc(s)) return false;
    if (looksLikeUtr(s)) return false;
    if (looksLikeNumeric(s)) return false;
    if (looksLikeMonth(s)) return false;
    // Must contain letters
    if (!/[A-Za-z]{2,}/.test(s)) return false;
    return true;
  };

  // Prefer the LAST candidate — in formats like INF/NEFT/UTR/IFSC/PURPOSE/NAME,
  // payer name is usually the rightmost text segment.
  // Score by: contains space (multi-word company) > length.
  const candidates = segments.filter(isCandidate);
  if (candidates.length === 0) return undefined;

  const scored = candidates.map((c, idx) => ({
    name: c,
    score: (c.includes(' ') ? 100 : 0) + c.length + idx * 2, // bias toward later segments
  }));
  scored.sort((a, b) => b.score - a.score);
  return scored[0].name;
}

export function parseICICIStatement(file: ArrayBuffer): BankTransaction[] {
  const wb = XLSX.read(file, { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

  // Find header row by looking for strong ICICI signals
  let headerIdx = -1;
  for (let i = 0; i < Math.min(rows.length, 30); i++) {
    const row = rows[i].map((c: any) => String(c).toLowerCase().trim());
    // Look for "transaction remarks" or ("tran" + "deposit") or "s.n."
    const hasRemarks = row.some((c: string) => c.includes('transaction remarks') || c.includes('remark'));
    const hasDeposit = row.some((c: string) => c.includes('deposit'));
    const hasTranId = row.some((c: string) => c.includes('tran') && c.includes('id'));
    const hasSN = row.some((c: string) => c === 's.n.' || c === 's no' || c === 'sn');

    if ((hasRemarks && hasDeposit) || (hasTranId && hasDeposit) || (hasSN && hasDeposit)) {
      headerIdx = i;
      break;
    }
  }
  if (headerIdx === -1) headerIdx = 16; // fallback row 17 (0-indexed 16)

  const headers = rows[headerIdx].map((h: any) => String(h).toLowerCase().trim());

  const colMap = {
    transactionId: headers.findIndex((h: string) => h.includes('tran') && h.includes('id')),
    valueDate: headers.findIndex((h: string) => h.includes('value') && h.includes('date')),
    transactionDate: headers.findIndex((h: string) => h.includes('transaction date') && !h.includes('posted')),
    postedDatetime: headers.findIndex((h: string) => h.includes('posted')),
    reference: headers.findIndex((h: string) => h.includes('cheque') || h.includes('ref')),
    narration: headers.findIndex((h: string) => h.includes('remark') || h.includes('narration') || h.includes('description')),
    withdrawal: headers.findIndex((h: string) => h.includes('withdrawal') || h.includes('debit')),
    deposit: headers.findIndex((h: string) => h.includes('deposit') || h.includes('credit')),
    balance: headers.findIndex((h: string) => h.includes('balance')),
  };

  const transactions: BankTransaction[] = [];

  for (let i = headerIdx + 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.length < 3) continue;

    const firstCell = String(row[0] || '').trim();
    if (firstCell.toLowerCase().includes('page total') || firstCell.toLowerCase().includes('legend')) break;

    const withdrawal = parseAmount(colMap.withdrawal >= 0 ? row[colMap.withdrawal] : 0);
    const deposit = parseAmount(colMap.deposit >= 0 ? row[colMap.deposit] : 0);

    if (withdrawal === 0 && deposit === 0) continue;

    const narration = String(colMap.narration >= 0 ? row[colMap.narration] : '').trim();
    const transactionType = deposit > 0 ? 'credit' as const : 'debit' as const;

    transactions.push({
      id: `TXN-${i}-${Date.now()}`,
      transactionId: String(colMap.transactionId >= 0 ? row[colMap.transactionId] : `${i}`).trim(),
      valueDate: parseDate(colMap.valueDate >= 0 ? row[colMap.valueDate] : ''),
      transactionDate: parseDate(colMap.transactionDate >= 0 ? row[colMap.transactionDate] : ''),
      postedDatetime: colMap.postedDatetime >= 0 ? String(row[colMap.postedDatetime]).trim() : undefined,
      referenceNumber: String(colMap.reference >= 0 ? row[colMap.reference] : '').trim(),
      narration,
      withdrawalAmount: withdrawal,
      depositAmount: deposit,
      balance: parseAmount(colMap.balance >= 0 ? row[colMap.balance] : 0),
      transactionType,
      utrNumber: extractUTR(narration),
      counterpartyName: extractCounterparty(narration),
      paymentMode: extractPaymentMode(narration),
      matchStatus: 'unmatched',
    });
  }

  return transactions;
}
